import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  Chat as ChatIcon,
  CreditCard as CreditCardIcon,
  BarChart as ResultIcon,
  Assignment as AssignmentIcon,
  Campaign as CampaignIcon,
  MenuBook as MenuBookIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import BackgoundGraphic from '../image/BackgroundGraphic.png';

const ParentSidebar = ({ availablePages }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(!availablePages || availablePages.length === 0);

  useEffect(() => {
    if (availablePages && availablePages.length > 0) {
      setLoading(false);
    }
  }, [availablePages]);

  // Normalize URL for comparison
  const normalizeUrl = (url) => {
    return url.replace(/^(\.\.\/)*/, '').replace(/^\//, '');
  };

  // Check if current page is active
  const isActive = (url) => {
    const normalizedUrl = normalizeUrl(url);
    const currentPath = location.pathname.replace(/^\//, '');
    return currentPath === normalizedUrl;
  };

  // Comprehensive icon mapping
  const getIconForForm = (formName) => {
    const formNameLower = formName.toLowerCase();

    if (formNameLower.includes('result')) return <ResultIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('fee') || formNameLower.includes('payment')) return <CreditCardIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('time') || formNameLower.includes('schedule')) return <CalendarIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('topic') || formNameLower.includes('tracker')) return <MenuBookIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('announcement') || formNameLower.includes('notice')) return <CampaignIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('chat') || formNameLower.includes('message')) return <ChatIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('assignment')) return <AssignmentIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('homework')) return <MenuBookIcon sx={{ fontSize: 24 }} />;

    return <InfoIcon sx={{ fontSize: 24 }} />;
  };

  const handleNavigation = (url) => {
    const normalizedUrl = normalizeUrl(url);
    navigate(`/${normalizedUrl}`);
  };

  const isDashboardActive = location.pathname === '/ParentDashboard';

  // Shared styles for every menu button
  const menuButtonSx = {
    borderRadius: "12px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    px: 2.5,
    py: 1.5,
    minHeight: "52px",
    "&:hover": {
      backgroundColor: "rgba(75,108,183,0.06)",
      transform: "translateX(4px)",
      boxShadow: "0 2px 8px rgba(75,108,183,0.12)",
    },
    "&.Mui-selected": {
      background: "linear-gradient(135deg, rgba(75,108,183,0.15) 0%, rgba(24,40,72,0.12) 100%)",
      borderLeft: "4px solid rgba(24,40,72,1)",
      paddingLeft: "16px",
      boxShadow: "0 4px 12px rgba(75,108,183,0.2)",
      "&:hover": {
        background: "linear-gradient(135deg, rgba(75,108,183,0.18) 0%, rgba(24,40,72,0.15) 100%)",
      },
    },
  };

  const menuTextSx = (active) => ({
    "& .MuiTypography-root": {
      fontWeight: active ? 700 : 600,
      fontSize: "0.95rem",
      letterSpacing: "0.3px",
      color: active ? "rgba(24,40,72,1)" : "rgba(75,108,183,1)",
    },
  });

  return (
    <Box sx={{ marginTop: "70px" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: 280,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            marginTop: "70px",
            border: "none",
            borderRight: "1px solid rgba(75,108,183,0.12)",
            boxShadow: "4px 0 16px rgba(75,108,183,0.08)",
            backgroundColor: "#fff",
            overflowX: "hidden",

            // Background image layer: sits BEHIND the content with its own opacity,
            // so the module names stay sharp and fully readable.
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: `url(${BackgoundGraphic}) no-repeat center center`,
              backgroundSize: "165%",
              backgroundPosition: "left calc(100% - 50px)",
              opacity: 0.25, // lower = fainter (try 0.1 to 0.25)
              pointerEvents: "none",
              zIndex: 0,
            },
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          // zIndex: 1 lifts the menu above the faded image layer
          <List sx={{ p: 2.5, mt: 1, position: "relative", zIndex: 1 }}>
            {/* Dashboard - Always visible and fixed */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={isDashboardActive}
                onClick={() => navigate('/ParentDashboard')}
                sx={menuButtonSx}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 44,
                    color: isDashboardActive ? "rgba(24,40,72,1)" : "rgba(75,108,183,0.8)",
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText primary="Dashboard" sx={menuTextSx(isDashboardActive)} />
              </ListItemButton>
            </ListItem>

            {/* Dynamic menu items from API */}
            {availablePages && availablePages.map((item) => {
              const itemActive = isActive(item.formUrl);

              return (
                <ListItem key={item.formId} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={itemActive}
                    onClick={() => handleNavigation(item.formUrl)}
                    sx={menuButtonSx}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 44,
                        color: itemActive ? "rgba(24,40,72,1)" : "rgba(75,108,183,0.8)",
                      }}
                    >
                      {getIconForForm(item.formName)}
                    </ListItemIcon>
                    <ListItemText primary={item.formName} sx={menuTextSx(itemActive)} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Drawer>
    </Box>
  );
};

export default ParentSidebar;